import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "./AgencyFonts.scss";
import { clientRoutes } from "../../../utils/routes";
import ProtectedComponent from "../../../components/ProtectedComponent";
import NavigationLayout from "../navigation";
import Loading from "../../loading/Loading";
import LeftArrowIcon from "../../../components/icons/LeftArrowIcon";
import RightArrowIcon from "../../../components/icons/RightArrowIcon";
import CloseIcon from "../../../components/icons/CloseIcon";
import { RootState } from "../../../redux/store";
import { FontApi } from "../../../utils/api/Font";
import { commonAction } from "../../../redux/features/commonSlice";
import AgencyFontsList from "./AgencyFontsList";
import ListTopButtonsAndSearch from "./ListTopButtonsAndSearch";
import AgencyFontsFilter from "./filter/AgencyFontsFilter";

let searchTimer: any;

interface stateType {
  isLoading: boolean;
  fonts: any[];
  totalPage: number;
  pageNumber: number;
  totalDocs: number;
  search: string;
}

const AgencyFonts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const resetFilter = useSelector(
    (state: RootState) => state?.common?.resetFilter,
  );

  const [state, setState] = useState<stateType>({
    isLoading: true,
    fonts: [],
    totalPage: 1,
    pageNumber: 1,
    totalDocs: 0,
    search: "",
  });

  const agency_id = searchParams.get("agency_id") || "";
  const agency_name = searchParams.get("agency_name") || "";
  const status = searchParams.get("status") || "";
  const start_date = searchParams.get("start_date") || "";
  const end_date = searchParams.get("end_date") || "";

  const filterValues = {
    agency_id,
    agency_name,
    status,
    startDate: start_date || "00-00-00",
    endDate: end_date || "00-00-00",
  };

  const isFilterActive =
    filterValues.agency_id ||
    filterValues.status ||
    (filterValues.startDate !== "00-00-00" &&
      filterValues.endDate !== "00-00-00");

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "00-00-00") return "";
    const parts = dateString.split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateString;
  };

  const formatLabel = (value: string) => {
    if (!value) return "";
    return value
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const getListDataApi = async (search: string, page: number) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    const res = await FontApi.getAgencyFontsList({
      page,
      search,
      agency_id,
      status,
      start_date,
      end_date,
    });

    if (res?.success && res?.data?.success) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        fonts: res?.data?.data?.docs || [],
        totalPage: res?.data?.data?.totalPages || 1,
        totalDocs: res?.data?.data?.totalDocs || 0,
        search,
      }));
    } else {
      setState((prev) => ({ ...prev, isLoading: false, fonts: [] }));
    }
  };

  const applyFiltersToUrl = (values: any) => {
    const navigationString =
      `agency-fonts?agency_id=${values?.agency_id || ""}` +
      `&agency_name=${values?.agency_name || ""}` +
      `&status=${values?.status || ""}` +
      `&start_date=${
        values?.startDate === "00-00-00" ? "" : values?.startDate
      }` +
      `&end_date=${values?.endDate === "00-00-00" ? "" : values?.endDate}`;

    navigate(`/${navigationString}`);
    dispatch(commonAction.resetFilter());
  };

  const handleRemoveAgency = () =>
    applyFiltersToUrl({ ...filterValues, agency_id: "", agency_name: "" });

  const handleRemoveStatus = () =>
    applyFiltersToUrl({ ...filterValues, status: "" });

  const handleRemoveDate = () =>
    applyFiltersToUrl({
      ...filterValues,
      startDate: "00-00-00",
      endDate: "00-00-00",
    });

  const handleClearAll = () => {
    navigate(clientRoutes.agencyFonts);
    dispatch(commonAction.resetFilter());
  };

  const onClickPreview = () =>
    setState({ ...state, pageNumber: state.pageNumber - 1 });

  const onClickNext = () =>
    setState({ ...state, pageNumber: state.pageNumber + 1 });

  const onChangeSearch = (e: any) => {
    let search = e?.target?.value;
    setState({ ...state, search, pageNumber: 1 });
  };

  useEffect(() => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      getListDataApi(state?.search, state?.pageNumber);
    }, 500);
  }, [state?.search, state?.pageNumber]);

  useEffect(() => {
    getListDataApi(state?.search, state?.pageNumber);
  }, [resetFilter, searchParams]);

  const startRange = (state.pageNumber - 1) * 20 + 1;
  const endRange = Math.min(state.pageNumber * 20, state.totalDocs);

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <AgencyFontsFilter />

        <div className="agency-fonts-list-container">
          <div className="agency-fonts-list-heading">
            <p>Agency fonts</p>
          </div>

          <ListTopButtonsAndSearch
            placeholder="Search by name or agency id"
            onChangeSearch={onChangeSearch}
            type="agency_fonts"
            reset_btn_link={clientRoutes?.agencyFonts}
          />

          {isFilterActive && (
            <div className="applied-filters-container">
              <p className="applied-label font-inter-400">Applied filters</p>

              <div className="chips-wrapper">
                {filterValues.agency_id && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Agency:{" "}
                      {filterValues.agency_name || filterValues.agency_id}
                    </span>
                    <div className="close-btn" onClick={handleRemoveAgency}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.status && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Status: {formatLabel(filterValues.status)}
                    </span>
                    <div className="close-btn" onClick={handleRemoveStatus}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.startDate !== "00-00-00" && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Date created: {formatDate(filterValues.startDate)} -{" "}
                      {formatDate(filterValues.endDate)}
                    </span>
                    <div className="close-btn" onClick={handleRemoveDate}>
                      <CloseIcon />
                    </div>
                  </div>
                )}

                <div
                  className="clear-all-btn font-inter-400"
                  onClick={handleClearAll}
                >
                  Clear all filters
                </div>
              </div>
            </div>
          )}

          {state?.isLoading ? (
            <div className="list-page-loading">
              <Loading />
            </div>
          ) : state.fonts.length === 0 ? (
            <div className="data-not-found">
              <p>List is empty!</p>
            </div>
          ) : (
            <AgencyFontsList fonts={state?.fonts} />
          )}

          {!state?.isLoading && (
            <div className="agency-fonts-pagination-wrapper">
              <p className="viewing-results font-inter-400">
                {state.totalDocs === 0
                  ? "No results"
                  : `Viewing ${startRange}-${endRange} of ${state.totalDocs} results`}
              </p>

              {state?.totalPage > 1 && (
                <div className="agency-fonts-list-pagination">
                  <button
                    className="preview-box pagination-button"
                    onClick={onClickPreview}
                    disabled={state?.pageNumber === 1}
                  >
                    <LeftArrowIcon />
                  </button>
                  <button
                    className="next-box pagination-button"
                    onClick={onClickNext}
                    disabled={state?.pageNumber === state.totalPage}
                  >
                    <RightArrowIcon />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default AgencyFonts;
